import { getLevelFromXP } from '@blind-kings/game-core';
import { onAuthChange } from '@aws-blocks/blocks/ui';
import { api, authApi } from 'aws-blocks';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import LoginModal from '../Components/LoginModal';
import type { AuthUser, UserData } from '../types/app';
import { useSnackBarContext } from './SnackBarContext';

export type AuthContextValue = {
  currentUser: AuthUser | undefined;
  userData: UserData | undefined;
  queryUserData: () => Promise<void>;
  signOut: () => Promise<boolean | undefined>;
  openLoginModal: () => void;
  onAuthenticated: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue>({
  currentUser: undefined,
  userData: undefined,
  queryUserData: async () => {},
  signOut: async () => undefined,
  openLoginModal: () => {},
  onAuthenticated: async () => {},
});

export const useAuthContext = () => useContext(AuthContext);

function toAuthUser(
  user: { userId?: string; username?: string; userSub?: string } | null | undefined
): AuthUser | undefined {
  if (!user) return undefined;
  const sub = (user as { userSub?: string }).userSub || user.userId || '';
  return {
    username: user.username || sub,
    userId: sub,
    attributes: { sub },
  };
}

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AuthUser | undefined>();
  const [userData, setUserData] = useState<UserData | undefined>();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const openLoginModal = () => setShowLoginModal(true);
  const { addSnackBar } = useSnackBarContext();

  const queryUserData = useCallback(async () => {
    if (!currentUser) return;
    try {
      const profile = (await api.getPlayerProfile()) as UserData & {
        xpBoostExpiration?: number;
        coinBoostExpiration?: number;
      };
      setUserData({
        ...profile,
        level: getLevelFromXP(Number(profile.xp ?? 0)),
        xpBoostDays:
          (Number(profile.xpBoostExpiration ?? 0) - Date.now()) / 1000 / 60 / 60 / 24,
        coinBoostDays:
          (Number(profile.coinBoostExpiration ?? 0) - Date.now()) / 1000 / 60 / 60 / 24,
      });
    } catch {
      addSnackBar({
        title: 'ERROR',
        message: 'Error getting user data. Please try again later.',
        variant: 'danger',
        timeout: 2500,
      });
    }
  }, [currentUser, addSnackBar]);

  const refreshAuth = useCallback(async () => {
    try {
      const state = await authApi.getAuthState();
      setCurrentUser(toAuthUser(state.user));
    } catch (err) {
      console.error(err);
      setCurrentUser(undefined);
    }
  }, []);

  const onAuthenticated = useCallback(async () => {
    await refreshAuth();
    setShowLoginModal(false);
  }, [refreshAuth]);

  const signOut = useCallback(async () => {
    try {
      await authApi.setAuthState({ action: 'signOut' });
      setCurrentUser(undefined);
      setUserData(undefined);
      return true;
    } catch {
      addSnackBar({
        title: 'ERROR',
        message: 'Could not sign out. Please try again.',
        variant: 'danger',
        timeout: 2500,
      });
    }
  }, [addSnackBar]);

  useEffect(() => {
    void refreshAuth();
    return onAuthChange(authApi, (user) => {
      setCurrentUser(toAuthUser(user));
    });
  }, [refreshAuth]);

  useEffect(() => {
    if (currentUser) void queryUserData();
    else setUserData(undefined);
  }, [currentUser, queryUserData]);

  useEffect(() => {
    if (currentUser && showLoginModal) setShowLoginModal(false);
  }, [currentUser, showLoginModal]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userData,
        queryUserData,
        signOut,
        openLoginModal,
        onAuthenticated,
      }}
    >
      {children}
      <LoginModal show={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </AuthContext.Provider>
  );
}
